import { observable, action } from 'mobx';
import { API_ACCOUNT_FINANCE_PARTNER, API_ACCOUNT_DETAIL_FINANCE_PARTNER } from 'api/endpoints';
import { FormikHelpers } from 'formik';
import { Partner } from './model';
import { FormStore } from 'stores/formStore';
import { RootStore } from 'stores/rootStore';
import { SingleValueResponse } from 'common/interfaces';
import { RentModalsStore } from 'stores/workflowStores/finances/rentModalsStore';

class PlanningFor extends FormStore<Partner> {
  accountId: string;
  detailMode?: boolean;
  rentModalsStore: RentModalsStore;

  constructor(rootStore: RootStore, rentModalsStore: RentModalsStore, accountId: string, detailMode?: boolean) {
    super(rootStore);
    this.rentModalsStore = rentModalsStore;
    this.accountId = accountId;
    this.detailMode = detailMode;
  }

  @observable planningFor: 'onlyMe' | 'meAndPartner' = 'onlyMe';

  setFinancesPartner = (partner?: Partner) => (this.rentModalsStore.partner = partner || null);

  @action setPlanningFor = (planningFor: 'onlyMe' | 'meAndPartner') => (this.planningFor = planningFor);

  @action fetchPartner = async () => {
    this.rentModalsStore.setLoading(true);
    const { get } = this.rootStore.stores.apiStore;
    const response = await get<Partner>(
      this.detailMode
        ? API_ACCOUNT_DETAIL_FINANCE_PARTNER(this.accountId)
        : API_ACCOUNT_FINANCE_PARTNER(this.accountId),
    );

    if (response?.data) {
      // ICe: all fields in partner are required, so if at least one of them is present, we assume, that the Partner is present
      this.setData(response.data);
      if (response.data.firstname) {
        this.setFinancesPartner(response.data);
        this.setPlanningFor('meAndPartner');
      } else {
        this.setFinancesPartner();
      }
    }
    this.rentModalsStore.setLoading(false);
  };

  submitPartner = async (values: Partner, formikHelpers: FormikHelpers<Partner>) => {
    const success = await this.submitData(
      this.detailMode
        ? API_ACCOUNT_DETAIL_FINANCE_PARTNER(this.accountId)
        : API_ACCOUNT_FINANCE_PARTNER(this.accountId),
      values,
      {
        validationPrefixDepth: 3,
        formikHelpers,
        reset: false,
        singleValueResponse: true,
      },
    );

    if (success) {
      await this.fetchPartner();
      this.rentModalsStore.onNextRentTab();
    }
  };

  deletePartner = async (_data: Partner, formikHelpers: FormikHelpers<Partner>) => {
    formikHelpers.setSubmitting(true);
    const response = await this.rootStore.stores.apiStore.delete<SingleValueResponse<boolean>>(
      this.detailMode
        ? API_ACCOUNT_DETAIL_FINANCE_PARTNER(this.accountId)
        : API_ACCOUNT_FINANCE_PARTNER(this.accountId),
    );
    formikHelpers.setSubmitting(false);
    if (response?.data?.value) {
      this.setFinancesPartner();
      this.rentModalsStore.onNextRentTab();
    }
  };
}

export default PlanningFor;
